import { useCallback, useEffect, useState, JSX } from 'react';
import { Badge, Button, Stack, VisuallyHidden } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';

import { Span, Title } from 'shared-components';
import { EventInfoStatus, RecordedInterviewSystemStatus, ServiceProviderType } from 'types';

import { UserRole } from '../../../store/auth/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { SectionProps, SectionInfoState } from './types';

interface Props {
  userRole: UserRole;
  serviceProviderType?: ServiceProviderType;
  infoState: SectionInfoState;
  givenName?: string;
  middleName?: string;
  surname?: string;
  studentNo?: string;
  checklistComplete: number;
  docsComplete: number;
  docsRequired: number;
  finDocsComplete: number;
  finDocsRequired: number;
  beforeFollowUpDocsComplete: number;
  beforeFollowUpDocsRequired: number;
  afterFollowUpDocsComplete: number;
  afterFollowUpDocsRequired: number;
  interviewComplete: number;
  visaPrepComplete: number;
  currentStage: string;
  isChecklistRisk: boolean;
  isInactiveChecklistRisk: boolean;
  isDocsRisk: boolean;
  isInactiveDocRisk: boolean;
  isFinDocsRisk: boolean;
  isInactiveFinDocRisk: boolean;
  isPreCasRisk: boolean;
  isInactivePreCasRisk: boolean;
  preCasFlags: number;
  hasVisaPrepBeenActivated: boolean;
  visaPrepActiveForBrand: boolean;
  isPreCasFormHidden: boolean;
  isPreDepartureDocsHidden: boolean;
  isAfterTravelDocsHidden: boolean;
  isGeneralDocFormHidden: boolean;
  isFinanceDocFormHidden: boolean;
  isScheduledInterviewsHidden: boolean;
  isRecordedInterviewHidden: boolean;
  configSections: any;
  onSectionChange: (id: string) => void;
  calendlyInterviewsInfoStatus: EventInfoStatus;
  recordedInterviewSystemStatus: RecordedInterviewSystemStatus;
}

function StudentLeftNav({
  userRole,
  serviceProviderType,
  infoState,
  givenName,
  middleName,
  surname,
  studentNo,
  checklistComplete,
  docsComplete,
  docsRequired,
  finDocsComplete,
  finDocsRequired,
  beforeFollowUpDocsComplete,
  beforeFollowUpDocsRequired,
  afterFollowUpDocsComplete,
  afterFollowUpDocsRequired,
  interviewComplete,
  visaPrepComplete,
  onSectionChange,
  currentStage,
  isChecklistRisk,
  isInactiveChecklistRisk,
  isDocsRisk,
  isInactiveDocRisk,
  isFinDocsRisk,
  isInactiveFinDocRisk,
  isPreCasRisk,
  isInactivePreCasRisk,
  preCasFlags,
  hasVisaPrepBeenActivated,
  visaPrepActiveForBrand,
  isPreCasFormHidden,
  isPreDepartureDocsHidden,
  isAfterTravelDocsHidden,
  isGeneralDocFormHidden,
  isFinanceDocFormHidden,
  isScheduledInterviewsHidden,
  isRecordedInterviewHidden,
  configSections,
  calendlyInterviewsInfoStatus,
  recordedInterviewSystemStatus,
}: Props): JSX.Element {
  const [sections, setSections] = useState<SectionProps[]>([]);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const isScheduledInterviewsVisible: boolean =
      !isScheduledInterviewsHidden && hasPerm(userRole, P.SCHEDULED_INTERVIEWS_VIEW);

    const isRecordedInterviewVisible: boolean =
      !isRecordedInterviewHidden && hasPerm(userRole, P.RECORDED_INTERVIEW_VIEW, serviceProviderType);

    const isInterviewSectionVisible: boolean = isScheduledInterviewsVisible || isRecordedInterviewVisible;

    const sectionData: { item: SectionProps; visible: boolean }[] = [
      {
        item: {
          id: 'info',
          label: configSections?.['CASChecklist']?.label ?? 'CAS Checklist',
          subLabel: '',
          description: 'CAS Checklist',
          status: 'Available',
          infoState,
        },
        visible: hasPerm(userRole, P.CAS_INFO_VIEW),
      },
      {
        item: {
          id: 'checklist',
          label: 'Info Check',
          subLabel: '',
          description: 'Information Check',
          status: 'Available',
          isRisk: isChecklistRisk,
          isInactiveRisk: isInactiveChecklistRisk,
          completeness: checklistComplete,
        },
        visible: hasPerm(userRole, P.CHECKLIST_VIEW),
      },
      {
        item: {
          id: 'general-docs',
          label: 'General Docs',
          description: 'General Documents',
          status: 'Available',
          isRisk: isDocsRisk,
          isInactiveRisk: isInactiveDocRisk,
          itemsComplete: docsComplete,
          itemsRequired: docsRequired,
        },
        visible: !isGeneralDocFormHidden && hasPerm(userRole, P.GENERAL_DOCUMENTS_VIEW),
      },
      {
        item: {
          id: 'financial-docs',
          label: 'Financial Docs',
          description: 'Financial Documents',
          status: 'Available',
          isRisk: isFinDocsRisk,
          isInactiveRisk: isInactiveFinDocRisk,
          itemsComplete: finDocsComplete,
          itemsRequired: finDocsRequired,
        },
        visible: !isFinanceDocFormHidden && hasPerm(userRole, P.FINANCE_DOCUMENTS_VIEW),
      },
      {
        item: {
          id: 'pre-cas',
          label: configSections?.['PreCASInterview']?.label ?? 'Questionnaire',
          description: configSections?.['PreCASInterview']?.label ?? 'Pre-CAS Questionnaire',
          status: 'Available',
          isRisk: isPreCasRisk,
          isInactiveRisk: isInactivePreCasRisk,
          completeness: interviewComplete,
          flags: preCasFlags,
        },
        visible: !isPreCasFormHidden && hasPerm(userRole, P.PRE_CAS_INTERVIEW_VIEW),
      },
      {
        item: {
          id: 'scheduled-interviews',
          label: 'Interviews',
          description: 'Interviews',
          status: 'Available',
          calendlyInterviewsInfoStatus,
          recordedInterviewSystemStatus,
          isScheduledInterviewsVisible,
          isRecordedInterviewVisible,
        },
        visible: isInterviewSectionVisible,
      },
      {
        item: {
          id: 'pre-departure-docs',
          label: 'Travel Docs',
          description: 'Pre-departure & Travel Documents',
          status: 'Available',
          itemsComplete: beforeFollowUpDocsComplete,
          itemsRequired: beforeFollowUpDocsRequired,
        },
        visible: !isPreDepartureDocsHidden && hasPerm(userRole, P.FOLLOW_UP_DOCUMENTS_VIEW),
      },
      {
        item: {
          id: 'after-travel-docs',
          label: 'Arrival Docs',
          description: 'Arrival Documents',
          status: 'Available',
          itemsComplete: afterFollowUpDocsComplete,
          itemsRequired: afterFollowUpDocsRequired,
        },
        visible: !isAfterTravelDocsHidden && hasPerm(userRole, P.FOLLOW_UP_DOCUMENTS_VIEW),
      },
      {
        item: {
          id: 'visa-prep',
          label: 'Visa Prep',
          description: 'Visa Preparation',
          status: hasVisaPrepBeenActivated ? 'Available' : 'Disabled',
          completeness: visaPrepComplete,
        },
        visible: hasPerm(userRole, P.VISA_PREP_VIEW) && visaPrepActiveForBrand,
      },
      {
        item: {
          id: 'audit-log',
          label: 'Activity Log',
          description: 'Activity Log',
          status: 'Available',
          buttonProps: {
            mt: 3,
          },
        },
        visible: hasPerm(userRole, P.VIEW_AUDIT_LOG),
      },
    ];
    const sections = sectionData.filter(section => section.visible).map(section => section.item);

    setSections(sections);
  }, [
    userRole,
    serviceProviderType,
    infoState,
    checklistComplete,
    docsComplete,
    docsRequired,
    finDocsComplete,
    finDocsRequired,
    interviewComplete,
    visaPrepComplete,
    isChecklistRisk,
    isInactiveChecklistRisk,
    isDocsRisk,
    isInactiveDocRisk,
    isFinDocsRisk,
    isInactiveFinDocRisk,
    isPreCasRisk,
    isInactivePreCasRisk,
    preCasFlags,
    hasVisaPrepBeenActivated,
    visaPrepActiveForBrand,
    isPreCasFormHidden,
    isGeneralDocFormHidden,
    isFinanceDocFormHidden,
    isPreDepartureDocsHidden,
    isAfterTravelDocsHidden,
    beforeFollowUpDocsComplete,
    beforeFollowUpDocsRequired,
    afterFollowUpDocsComplete,
    afterFollowUpDocsRequired,
    isScheduledInterviewsHidden,
    isRecordedInterviewHidden,
    configSections,
    calendlyInterviewsInfoStatus,
    recordedInterviewSystemStatus,
    currentStage,
  ]);

  const onActiveSection = useCallback(
    (sectionId: string) => {
      setActiveSection(sectionId);
      onSectionChange(sectionId);
    },
    [onSectionChange],
  );

  useEffect(() => {
    if (!activeSection && sections[0]) {
      onActiveSection(sections[0].id);
    }
  }, [activeSection, sections, onActiveSection]);

  const onSectionSelect = (sectionId: string): void => {
    const section = sections.find(sectionProp => sectionProp.id === sectionId);
    if (!section || section.status === 'Disabled') {
      return;
    }
    onActiveSection(sectionId);
  };

  const getIconProps = (section: SectionProps) => {
    const { isRisk, isInactiveRisk, flags } = section;

    if (isRisk || flags) {
      if (isInactiveRisk) {
        return {
          icon: faFileMagnifyingGlass,
          label: 'Highlighted issue(s) marked as OK',
        };
      }

      return {
        icon: faFileMagnifyingGlass,
        label: 'Highlighted issue(s)',
      };
    }

    return { icon: null };
  };

  const canPassScheduledInterviews = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_PASS);
  const canResitScheduledInterviews = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_RESIT);
  const canFailScheduledInterviews = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_FAIL);
  const canPassRecordedInterviews = hasPerm(userRole, P.RECORDED_INTERVIEW_PASS);
  const canResitRecordedInterviews = hasPerm(userRole, P.RECORDED_INTERVIEW_RESIT);
  const canFailRecordedInterviews = hasPerm(userRole, P.RECORDED_INTERVIEW_FAIL);

  const getBadgeProps = (section: SectionProps) => {
    const {
      infoState,
      completeness,
      itemsComplete,
      itemsRequired,
      calendlyInterviewsInfoStatus,
      recordedInterviewSystemStatus,
      isScheduledInterviewsVisible,
      isRecordedInterviewVisible,
      isRisk,
      isInactiveRisk,
    } = section;

    switch (infoState) {
      case SectionInfoState.Complete:
        return {
          colorScheme: 'green',
          children: 'Complete',
        };
      case SectionInfoState.Conflict:
        return {
          colorScheme: 'red',
          children: 'Conflict',
        };
      case SectionInfoState.InProgress:
        return {
          colorScheme: 'gray',
          children: 'To do',
        };
      case SectionInfoState.Pending:
        return {
          colorScheme: 'cyan',
          children: 'Pending',
        };
      case SectionInfoState.Update:
        return {
          colorScheme: 'cyan',
          children: 'Update',
        };
      default:
        break;
    }

    if (
      (calendlyInterviewsInfoStatus && isScheduledInterviewsVisible) ||
      (recordedInterviewSystemStatus && isRecordedInterviewVisible)
    ) {
      if (calendlyInterviewsInfoStatus === EventInfoStatus.BOOKED) {
        return {
          colorScheme: 'gray',
          children: 'Booked',
        };
      }
      if (recordedInterviewSystemStatus === RecordedInterviewSystemStatus.ATTEMPTED) {
        return {
          colorScheme: 'orange',
          children: 'Attempted',
        };
      }
      if (recordedInterviewSystemStatus === RecordedInterviewSystemStatus.EXCEEDED) {
        return {
          colorScheme: 'gray',
          children: 'Exceeded',
        };
      }
      if (recordedInterviewSystemStatus === RecordedInterviewSystemStatus.DELETED) {
        return {
          colorScheme: 'gray',
          children: 'Deleted',
        };
      }
      if (recordedInterviewSystemStatus === RecordedInterviewSystemStatus.RECORDED) {
        return {
          colorScheme: 'gray',
          children: 'Recorded',
        };
      }
      if (
        calendlyInterviewsInfoStatus === EventInfoStatus.INVITED ||
        recordedInterviewSystemStatus === RecordedInterviewSystemStatus.INVITED
      ) {
        return {
          colorScheme: 'gray',
          children: 'Invited',
        };
      }
      if (
        (canFailScheduledInterviews && calendlyInterviewsInfoStatus === EventInfoStatus.FAILED) ||
        (canFailRecordedInterviews && recordedInterviewSystemStatus === RecordedInterviewSystemStatus.FAILED)
      ) {
        return {
          colorScheme: 'red',
          children: 'Failed',
        };
      }
      if (
        (canPassScheduledInterviews && calendlyInterviewsInfoStatus === EventInfoStatus.PASSED) ||
        (canPassRecordedInterviews && recordedInterviewSystemStatus === RecordedInterviewSystemStatus.PASSED)
      ) {
        return {
          colorScheme: 'green',
          children: 'Passed',
        };
      }
      if (
        (canResitScheduledInterviews && calendlyInterviewsInfoStatus === EventInfoStatus.RESIT) ||
        (canResitRecordedInterviews && recordedInterviewSystemStatus === RecordedInterviewSystemStatus.RESIT)
      ) {
        return {
          colorScheme: 'orange',
          children: 'Resit',
        };
      }
      if (
        calendlyInterviewsInfoStatus === EventInfoStatus.NONE ||
        recordedInterviewSystemStatus === RecordedInterviewSystemStatus.NONE
      ) {
        return {
          colorScheme: 'gray',
          children: 'None',
        };
      }
    }

    if (itemsRequired >= 0) {
      const label = `${itemsComplete}/${itemsRequired}`;

      if (isRisk && !isInactiveRisk) {
        return {
          colorScheme: 'orange',
          children: label,
        };
      }

      if (itemsComplete > 0) {
        return {
          colorScheme: itemsComplete < itemsRequired ? 'gray' : 'green',
          children: label,
        };
      }
      return {
        colorScheme: 'gray',
        children: label,
      };
    }

    if (completeness >= 0) {
      const label = `${completeness}%`;
      if (completeness > 0) {
        if (isRisk && !isInactiveRisk) {
          return {
            colorScheme: 'orange',
            children: label,
          };
        }
        return {
          colorScheme: completeness < 100 ? 'gray' : 'green',
          children: label,
        };
      }
      return {
        colorScheme: 'gray',
        children: label,
      };
    }

    return {
      children: null,
    };
  };

  let activeLabel: string;
  if (!activeSection) {
    activeLabel = '';
  } else {
    activeLabel = sections.find(({ id }) => id === activeSection)?.description ?? '';
  }

  const getMiddleName = middleName ? ` ${middleName}` : '';

  const title = `${givenName} ${getMiddleName} ${surname} (${studentNo}) ${activeLabel}`;

  return (
    <>
      <Title title={title} />

      <Stack spacing={0} px={'sidebarPxSmall'} py={{ base: 0, md: 5 }} overflowY={'auto'} className="nav-scrollbar">
        {sections.map((section, index) => {
          const { children, colorScheme } = getBadgeProps(section);
          const { icon, label } = getIconProps(section);

          return (
            <Button
              data-selector={`${section.id}`}
              variant={'sidebar'}
              size={'sm'}
              key={index}
              isActive={section.id === activeSection}
              isDisabled={section.status === 'Disabled'}
              onClick={event => {
                event.preventDefault();
                if (typeof onSectionSelect === 'function') {
                  onSectionSelect(section.id);
                }
              }}
              {...section.buttonProps}
            >
              <Span overflow={'hidden'} textOverflow={'ellipsis'}>
                {section.label}
              </Span>

              {children && (
                <Badge ml={'auto'} size={'xs'} colorScheme={colorScheme} data-selector="completeness">
                  {icon && (
                    <Span mr={0.5} opacity={0.5}>
                      <FontAwesomeIcon icon={icon} />
                    </Span>
                  )}

                  <VisuallyHidden>- completeness: </VisuallyHidden>
                  {children}

                  {icon && label && <VisuallyHidden> ({label})</VisuallyHidden>}
                </Badge>
              )}
            </Button>
          );
        })}
      </Stack>
    </>
  );
}

export default StudentLeftNav;
